// 청구서 납부 과정 > 1. 청구서 내용 확인

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

import fonts from "@/design/typography";
import colors from "@/design/colors";
import { Page_settings } from "@/config/page_settings";
import NaverPayIcon from "@/components/icons/naver_pay";
import KakaoPayIcon from "@/components/icons/kakao_pay";
import { ChevronLeft, ChevronRight } from "@/components/icons/chevron";
import Button from "@/components/button";
import ResetIcon from "@/components/icons/reset";
import IconX from "@/components/icons/x";
import { useClickOutsideCloseModal } from "@/hooks/useClickOutsideCloseElement";

import CariPayLogoBlack from "@/components/icons/cariPayLogoBlack";
import { buildReturnUrl } from "./buildReturnUrl";
import { paymentLink } from "../paymentLink";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */
type PaymentMethod = "CARD" | "NAVER_PAY" | "KAKAO_PAY" | null;

const PaymentResultPage = () => {
  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  const navigate = useNavigate();
  const context: any = useContext(Page_settings);

  // ref
  const receiptBottomSheetRef = useRef<HTMLDivElement>(null);

  // 결제 타입
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [showReceiptBottomSheet, setShowReceiptBottomSheet] = useState(false);

  const [paymentResultData, setPaymentResultData] = useState(
    {
      processStatus: "", // "SUCCESS" | "CANCEL"
      processStatusName: "",
      storeName: "",
      approvalAmountFormat: "",
      approvalCancelDatetimeFormat: "",
      cancelReason: "",
      approvalDatetimeFormat: "",
      mobileNo: "",
      approvalNumber: "",
      issuerName: "",
      accepterName: "",
      orderType: "" as "BILL" | "SHOP" | "",
    }
  );

  const [receiptData, setReceiptData] = useState(
    { 
      processStatus: "", // 또는 "CANCEL"
      processStatusName: "",
      approvalCancelDatetimeFormat: "",
      cancelAmountFormat: "",
      approvalDatetimeFormat: "",
      approvalAmountFormat: "",
      supplyAmountFormat: "",
      taxChargeFormat: "",
      approvalNumber: "",
      installmentMonthName: "",
      issuerName: "",
      accepterName: "",
      methodName: "",
      // cardNumber: "",

      storeName: "",
      ceoName: "",
      bizRegNum: "",
      telNum: "",
    }
  );

  // // 결제 데이터
  // const paymentResultData = {
  //   status: "SUCCESS", // "SUCCESS" | "CANCEL"
  //   storeName: "(주)고트헤이븐",
  //   approvalAmountFormat: "270,001원",
  //   approvalCancelDatetimeFormat: "2025.05.15 14:19:11",
  //   cancelReason: "고객 요청에 의한 취소",
  //   date: "2025.05.15 14:19:11",
  //   mobileNo: "010-1234-5678",
  //   approvalNumber: "12309809",
  //   issuerName: "카카오페이",
  //   accepterName: "신한카드",
  // };

  // 영수증 데이터
  // const receiptData = {
  //   status: "CANCEL", // 또는 "CANCEL"
  //   approvalCancelDatetimeFormat: "2025년 2월 20일 13:00",
  //   cancelAmountFormat: "270,000원",
  //   approvalDatetimeFormat: "2025년 2월 18일 16:19",
  //   approvalAmountFormat: "270,000원",
  //   supplyAmountFormat: "100원",
  //   taxChargeFormat: "100원",
  //   approvalNumber: "5968435",
  //   installmentMonthName: "일시불",
  //   issuerName: "카카오페이",
  //   accepterName: "신한카드",
  //   methodName: "모바일 간편결제",
  //   cardNumber: "123412******34",

  //   company: {
  //     name: "(주)고트헤이븐",
  //     ceo: "차형호",
  //     regNumber: "261-81-01342",
  //     mobileNo: "010-1234-9999",
  //     address: "어쩌고 금천구 어쩌고 어쩌고 저쩌고",
  //   },
  // };

  /* **********************************
   * HOOKS
   ********************************** */
  useClickOutsideCloseModal([receiptBottomSheetRef], () => {
    setShowReceiptBottomSheet(false);
  });

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */
  useEffect(() => {
    const { txId, accessToken } = paymentLink();

    if (!txId) {
      return;
    }
    
    context.post(
      `/payment/getPaymentComplete`,
      {
        txId,
        accessToken,
      },
      (response) => {
        if(!response || !response.processStatus){
          return;
        }
        setPaymentResultData(response);
        setReceiptData(response);

        const returnTo = response.returnDisplayYn === "Y" ? buildReturnUrl(response) : null;
        if (returnTo) {
          window.location.href = returnTo;
        }
                
        // if(response.processStatus === "APPROVE_COMPLETE"){
        //   clearInterval(intervalRef.current);
        //   intervalRef.current = null;
        //   navigate("/payments/result");
        // }else if(response.processStatus === "APPROVE_FAIL"){
        //   clearInterval(intervalRef.current);
        //   intervalRef.current = null;
        //   navigate("/payments/error");
        // }else{

        // }
        
        //setListSearchStatus((prev) => !prev);
      }
    );
    // const paymentMethod = rootStore.paymentStore.paymentMethod;
    //setPaymentMethod(paymentMethod);
  }, []);

  /* **********************************
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ********************************** */

  return (
    <Container>
      <Wrapper>
        <HeaderWrapper>
          <CariPayLogoBlack width="148" height="28" />
        </HeaderWrapper>

        <ResultBox>
          <Tag $isCancel={paymentResultData.processStatus}>
            {paymentResultData.processStatus === "APPROVE_COMPLETE" ? "결제 완료" : "취소 완료"}
          </Tag>

          <Title>
            {paymentResultData.processStatus === "APPROVE_COMPLETE"
              ? "결제가 완료되었습니다"
              : "결제가 취소되었습니다"}
          </Title>

          <Info>
            <Row>
              <Label>{paymentResultData.storeName}</Label>
              <Value
                style={{
                  textDecoration:
                    paymentResultData.processStatus === "CANCEL_COMPLETE"
                      ? "line-through"
                      : "none",
                  ...fonts.body1Medium,
                  fontSize: "18px",
                }}
              >
                {paymentResultData.approvalAmountFormat}원
              </Value>
            </Row>

            <Divider />

            {paymentResultData.processStatus === "CANCEL_COMPLETE" && (
              <>
                <Row>
                  <Label>취소일시</Label>
                  <Value>{paymentResultData.approvalCancelDatetimeFormat}</Value>
                </Row>
                <Row>
                  <Label>취소사유</Label>
                  <Value>{paymentResultData.cancelReason}</Value>
                </Row>
                <Divider />
              </>
            )}

            <Row>
              <Label>결제일시</Label>
              <Value>{paymentResultData.approvalDatetimeFormat}</Value>
            </Row>
            <Row>
              <Label>전화번호</Label>
              <Value>{paymentResultData.mobileNo}</Value>
            </Row>
            <Row>
              <Label>승인번호</Label>
              <Value>{paymentResultData.approvalNumber}</Value>
            </Row>
            <Row>
              <Label>발급사</Label>
              <Value>{paymentResultData.issuerName}</Value>
            </Row>
            <Row>
              <Label>카드사명</Label>
              <Value>{paymentResultData.accepterName}</Value>
            </Row>
          </Info>

          <ReceiptButton
            type="button"
            $isCancel={paymentResultData.processStatus}
            onClick={() => setShowReceiptBottomSheet(true)}
          >
            영수증 보기
          </ReceiptButton>

          {/* 샵 분기: 결제 완료 페이지 배송 안내 (츄잉샵 §1-7) */}
          {paymentResultData.orderType === "SHOP" &&
            paymentResultData.processStatus === "APPROVE_COMPLETE" && (
              <ShopShippingCaption>
                결제 완료된 상품은 학원으로 배송됩니다.
              </ShopShippingCaption>
            )}
        </ResultBox>

        {showReceiptBottomSheet && (
          <>
            {/* 배경 dimmed 처리 */}
            <DimmedOverlay />
            <ReceiptBottomSheet ref={receiptBottomSheetRef}>
              <SheetHeader>
                <span>영수증</span>
                <IconX
                  color="#333"
                  width="24px"
                  height="24px"
                  onClick={() => setShowReceiptBottomSheet(false)}
                  style={{ cursor: "pointer" }}
                />
              </SheetHeader>

              <ReceiptContainer>
                {/* 결제 정보 */}
                <Section>
                  <SectionHeader>
                    <span style={{ display: "flex", alignItems: "center" }}>
                      결제 정보
                    </span>
                    {receiptData.processStatus === "APPROVE_COMPLETE" ? (
                      <StatusTag>결제 완료</StatusTag>
                    ) : (
                      <StatusTag $isCancel>결제 취소</StatusTag>
                    )}
                  </SectionHeader>
                  {receiptData.processStatus === "CANCEL_COMPLETE" && (
                    <>
                      <Field label="취소일시" value={receiptData.approvalCancelDatetimeFormat} />
                      <Field
                        label="취소금액"
                        value={receiptData.cancelAmountFormat}
                      />
                      <Divider style={{ margin: "10px 0" }} />
                    </>
                  )}
                  <Field label="결제일시" value={receiptData.approvalDatetimeFormat} />
                  <Field label="결제금액" value={receiptData.approvalAmountFormat} />
                  <Field label="공급가액" value={receiptData.supplyAmountFormat} />
                  <Field label="부가세" value={receiptData.taxChargeFormat} />
                  <Field label="승인번호" value={receiptData.approvalNumber} />
                  <Field label="할부개월" value={receiptData.installmentMonthName} />
                  <Field label="발급사" value={receiptData.issuerName} />
                  <Field label="카드사명" value={receiptData.accepterName} />
                  <Field label="결제수단" value={receiptData.methodName} />
                </Section>

                {/* 사업자 정보 */}
                <Section>
                  <SectionHeader>사업자 정보</SectionHeader>
                  <Field label="사업자명" value={receiptData.storeName} />
                  <Field label="대표자" value={receiptData.ceoName} />
                  <Field
                    label="사업자번호"
                    value={receiptData.bizRegNum}
                  />
                  <Field label="전화번호" value={receiptData.telNum} />
                </Section>

                {/* Copyright */}
                <Copyright>
                  Copyright © 2025 GOATHEAVEN All Rights Reserved.
                </Copyright>
              </ReceiptContainer>
            </ReceiptBottomSheet>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

export default PaymentResultPage;

// 페이지 전체
const Container = styled.div`
  max-width: 600px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  background: ${colors.gray50};
  overflow-y: auto;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background: ${colors.gray50};
  padding: 24px 20px;
  box-sizing: border-box;
`;

const HeaderWrapper = styled.div`
  margin-bottom: 24px;
`;

const ResultBox = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 40px 16px 20px 16px;
  text-align: center;
`;

const Tag = styled.div<{ $isCancel?: string }>`
  display: inline-block;
  padding: 6px 12px;
  background: ${({ $isCancel }) =>
    $isCancel === "CANCEL_COMPLETE" || $isCancel === "STORE_DELETE" ? colors.red50 : colors.blue50};
  color: ${({ $isCancel }) =>
    $isCancel === "CANCEL_COMPLETE" || $isCancel === "STORE_DELETE" ? colors.warning : colors.blue600};
  border-radius: 999px;
  margin-bottom: 28px;

  font-family: ${fonts.body2Medium.fontFamily};
  line-height: ${fonts.body2Medium.lineHeight};
  font-size: ${fonts.body2Medium.fontSize};
`;

const Title = styled.div`
  font-family: ${fonts.heading4SemiBold.fontFamily};
  line-height: ${fonts.heading4SemiBold.lineHeight};
  font-size: ${fonts.heading4SemiBold.fontSize};
  color: ${colors.gray900};
  margin-bottom: 50px;
`;

const Info = styled.div`
  text-align: left;
  margin-top: 16px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 12px;
`;

const Label = styled.div`
  color: ${colors.gray600};
  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: 14px;
`;

const Value = styled.div`
  color: ${colors.gray900};
  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: 14px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${colors.gray100};
  margin: 20px 0;
`;

const ReceiptButton = styled.button<{ $isCancel?: string }>`
  width: 100%;
  border: 0;
  margin-top: 24px;
  background: ${({ $isCancel }) =>
    $isCancel === "CANCEL_COMPLETE" || $isCancel === "STORE_DELETE" ? colors.warning50 : colors.blue50};
  color: ${({ $isCancel }) =>
    $isCancel === "CANCEL_COMPLETE" || $isCancel === "STORE_DELETE" ? colors.warning : colors.blue600};
  padding: 13px 0;
  border-radius: 8px;
  font-family: ${fonts.body2Medium.fontFamily};
  line-height: ${fonts.body2Medium.lineHeight};
  font-size: 16px;
  cursor: pointer;
`;

// 영수증 보기 바텀시트
const ReceiptBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 600px;
  margin: 0 auto;
  background-color: ${colors.white};
  padding: 28px 20px 28px 20px;
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.35s ease-out;
  z-index: 1000;

  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }
`;

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.gray900};
  margin-bottom: 20px;

  font-family: ${fonts.body1SemiBold.fontFamily};
  line-height: ${fonts.body1SemiBold.lineHeight};
  font-size: ${fonts.body1SemiBold.fontSize};
`;

const Field = ({ label, value }: { label: string; value: string }) => (
  <FieldRow>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{value}</FieldValue>
  </FieldRow>
);

const ReceiptContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 28px;
`;

const Section = styled.div`
  background: ${colors.gray50};
  padding: 20px 16px;
  border-radius: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${fonts.body2SemiBold.fontFamily};
  font-size: ${fonts.body2SemiBold.fontSize};
  color: ${colors.gray900};
  margin-bottom: 20px;
`;

const StatusTag = styled.div<{ $isCancel?: boolean }>`
  width: fit-content;
  padding: 4px 10px;
  background: ${({ $isCancel }) =>
    $isCancel ? colors.warning50 : colors.blue50};
  color: ${({ $isCancel }) => ($isCancel ? colors.warning : colors.blue600)};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;

  font-family: ${fonts.body3Regular.fontFamily};
  line-height: ${fonts.body3Regular.lineHeight};
  font-size: ${fonts.body3Regular.fontSize};
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 6px;

  &:last-of-type {
    margin-bottom: 0;
  }

  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: 14pxg;
`;

const FieldLabel = styled.div`
  color: ${colors.gray600};
`;

const FieldValue = styled.div`
  color: ${colors.gray900};
`;

const Copyright = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${colors.gray50};
  height: 42px;
  border-radius: 12px;
  padding: 16px;

  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: ${fonts.detail2Medium.lineHeight};
  font-size: ${fonts.detail2Medium.fontSize};
`;

// 샵 배송 안내 캡션 (§1-7)
const ShopShippingCaption = styled.div`
  margin-top: 16px;
  color: ${colors.gray500};
  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: ${fonts.detail2Medium.lineHeight};
  font-size: ${fonts.detail2Medium.fontSize};
`;

// 배경 dimmed 처리
const DimmedOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4); // 적당한 어두움
  z-index: 999; // SignatureBottomSheet보다 낮게
`;
